import React, { useState, useEffect } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  CircularProgress,
  TablePagination,
  makeStyles,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@material-ui/core";
import PlayForWorkIcon from "@material-ui/icons/PlayForWork";
import validateDateAndFormat from "utils/validateDateAndFormat";
import DateRangePickerACCB from "Clients/ACCB/Admin/Report/DateRangePickerACCB";
import { getScrutinyList } from "../../service";
import { safeDateFormat, downloadDefectMemo } from "../../utils";
import ScrutinyCheck from "./ScrutinyCheck";

const MIN_FILTER_DATE = new Date(2020, 0, 1);

const SCRUTINY_STATUS_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Not Checked", value: "not_checked" },
  { label: "Defective", value: "defective" },
  { label: "Approved", value: "approved" }
];

const useStyles = makeStyles((theme) => ({
  tableCell: {
    borderRight: "none",
    borderLeft: "none",
    padding: "10px",
    fontSize: "0.875rem",
    borderBottom: "1px solid #E5E5E5",
    // overflowWrap (not wordBreak: break-word) so long words still wrap
    // without letting the column collapse to one character on phones
    overflowWrap: "break-word"
  },
  nowrapCell: {
    whiteSpace: "nowrap"
  },
  docNameCell: {
    minWidth: 180
  },
  uploadedByCell: {
    minWidth: 120
  },
  outerHeader: {
    backgroundColor: "#eeeeee"
  },
  outerHeaderText: {
    color: TEXT.primary,
    fontWeight: 600
  },
  statusBadge: {
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: 4
  },
  placeholderText: {
    color: TEXT.muted,
    fontSize: "0.875rem"
  },
  errorText: {
    fontSize: "0.875rem",
    color: TEXT.error
  },
  filtersContainer: {
    padding: "1rem",
    display: "flex",
    gap: "15px",
    alignItems: "center",
    borderBottom: "1px solid #E5E5E5",
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap"
    }
  },
  // On phones and tablets Status takes its own row so the date fields below
  // get the full width instead of being squeezed beside it
  statusFilter: {
    minWidth: 180,
    maxWidth: "22%",
    flex: 1,
    [theme.breakpoints.down("sm")]: {
      flexBasis: "100%",
      maxWidth: "none"
    }
  }
}));

const getStatusChip = (status, classes, rowId, onDownloadClick) => {
  if (!status) return "—";

  const s = status.toLowerCase();

  if (s === "approved") {
    return (
      <Chip
        label="Approved"
        className={classes.statusBadge}
        style={{ backgroundColor: "#e8f5e9", color: TEXT.success }}
        size="small"
      />
    );
  }

  if (s === "defective") {
    return (
      <Box display="inline-flex" alignItems="center" style={{ gap: "0.25rem" }}>
        <Chip
          label="Defective"
          className={classes.statusBadge}
          style={{ backgroundColor: "#ffebee", color: TEXT.error }}
          size="small"
        />
        <IconButton
          size="small"
          onClick={(e) => onDownloadClick(e, rowId)}
          style={{ padding: 4 }}
          title="Download Defect Memo"
        >
          <PlayForWorkIcon fontSize="small" color="primary" />
        </IconButton>
      </Box>
    );
  }

  // default / not_checked
  return (
    <Chip
      label="Not Checked"
      className={classes.statusBadge}
      style={{ backgroundColor: "#eeeeee", color: TEXT.muted }}
      size="small"
    />
  );
};

export default function Scrutiny({ pageSize = 10, onRowClick, profile }) {
  const classes = useStyles();
  const [documents, setDocuments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [status, setStatus] = useState("All");
  const [uploadedFrom, setUploadedFrom] = useState(null);
  const [uploadedTo, setUploadedTo] = useState(null);

  const [screen, setScreen] = useState("list");
  const [selectedDisputeId, setSelectedDisputeId] = useState(null);
  const [selectedScrutinyId, setSelectedScrutinyId] = useState(null);

  const today = new Date();

  // API expects YYYY-MM-DD; an incomplete/invalid picker value formats to "".
  const uploadedFromParam = validateDateAndFormat(uploadedFrom, "yyyy-MM-dd");
  const uploadedToParam = validateDateAndFormat(uploadedTo, "yyyy-MM-dd");
  const dateRangeError = Boolean(
    uploadedFromParam && uploadedToParam && uploadedFromParam > uploadedToParam
  );

  const handleDownloadDefectMemo = (e, scrutinyId) => {
    e.stopPropagation();
    setError("");
    downloadDefectMemo(scrutinyId, setError);
  };

  const fetchScrutinyData = (pageIndex) => {
    setIsLoading(true);
    setError("");

    const params = { page: pageIndex + 1, page_size: pageSize };
    if (status && status !== "All") params.status = status;
    if (uploadedFromParam) params.uploaded_from = uploadedFromParam;
    if (uploadedToParam) params.uploaded_to = uploadedToParam;

    getScrutinyList(params)
      .then((response) => {
        const data = response.data;
        if (data && data.results) {
          setDocuments(data.results);
          setTotalCount(data.count || 0);
        } else {
          setDocuments([]);
          setTotalCount(0);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching scrutiny records:", err);
        setError("Failed to load Scrutiny records.");
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (screen !== "list") return;
    if (dateRangeError) {
      setIsLoading(false);
      return;
    }
    fetchScrutinyData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, screen, status, uploadedFromParam, uploadedToParam, dateRangeError]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Any filter change puts us back on the first page.
  const handleStatusChange = (event) => {
    setPage(0);
    setStatus(event.target.value);
  };

  const handleUploadedFromChange = (date) => {
    setPage(0);
    setUploadedFrom(date);
  };

  const handleUploadedToChange = (date) => {
    setPage(0);
    setUploadedTo(date);
  };

  const handleRowClick = (row) => {
    setSelectedDisputeId(row.dispute_id);
    setSelectedScrutinyId(row.id);
    setScreen("check");
    if (onRowClick) {
      onRowClick(row);
    }
  };

  if (screen === "check") {
    return (
      <ScrutinyCheck
        scrutinyId={selectedScrutinyId}
        disputeId={selectedDisputeId}
        onBack={() => setScreen("list")}
        profile={profile}
      />
    );
  }

  const renderTableBody = () => {
    if (documents.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center" style={{ padding: 32 }}>
            <Typography className={classes.placeholderText}>
              No scrutiny record is found for this criteria.
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return documents.map((row, index) => {
      const serialNumber = page * pageSize + index + 1;
      return (
        <TableRow
          key={row.id || index}
          hover
          onClick={() => handleRowClick(row)}
          style={{ cursor: "pointer" }}
        >
          <TableCell className={`${classes.tableCell} ${classes.nowrapCell}`}>
            {serialNumber}
          </TableCell>
          <TableCell className={`${classes.tableCell} ${classes.nowrapCell}`}>
            {row.case_number || "—"}
          </TableCell>
          <TableCell className={`${classes.tableCell} ${classes.docNameCell}`}>
            {row.doc_name || "—"}
          </TableCell>
          <TableCell className={`${classes.tableCell} ${classes.uploadedByCell}`}>
            {row.uploaded_by || "—"}
          </TableCell>
          <TableCell className={`${classes.tableCell} ${classes.nowrapCell}`}>
            {safeDateFormat(row.uploaded_at)}
          </TableCell>
          <TableCell className={`${classes.tableCell} ${classes.nowrapCell}`}>
            {safeDateFormat(row.reviewed_at)}
          </TableCell>
          <TableCell className={`${classes.tableCell} ${classes.nowrapCell}`}>
            {getStatusChip(row.status, classes, row.id, handleDownloadDefectMemo)}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Box>
      <Paper style={{ borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
        <Box className={classes.filtersContainer}>
          <FormControl
            size="small"
            margin="none"
            variant="outlined"
            className={classes.statusFilter}
          >
            <InputLabel>{"Status"}</InputLabel>
            <Select
              label={"Status"}
              name="status"
              value={status}
              onChange={handleStatusChange}
            >
              {SCRUTINY_STATUS_OPTIONS.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <DateRangePickerACCB
            startDate={uploadedFrom}
            setStartDate={handleUploadedFromChange}
            endDate={uploadedTo}
            setEndDate={handleUploadedToChange}
            startLabel="Uploaded From"
            endLabel="Uploaded To"
            minDate={MIN_FILTER_DATE}
            maxDate={today}
          />
        </Box>

        <Box
          style={{
            padding: "0.5rem 1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem"
          }}
        >
          {dateRangeError ? (
            <Typography className={classes.errorText}>
              "Uploaded From" cannot be later than "Uploaded To".
            </Typography>
          ) : (
            <Box />
          )}
        </Box>

        {isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            style={{ gap: "1rem" }}
          >
            <CircularProgress size={40} />
            <Typography className={classes.placeholderText}>
              Loading scrutiny documents...
            </Typography>
          </Box>
        ) : error ? (
          <Box p={4} textAlign="center">
            <Typography className={classes.errorText}>{error}</Typography>
          </Box>
        ) : (
          <TableContainer style={{ overflowX: "auto" }}>
            <Table aria-label="scrutiny table" style={{ maxWidth: "100%", marginTop: 0 }}>
              <TableHead>
                <TableRow className={classes.outerHeader}>
                  <TableCell className={`${classes.tableCell} ${classes.outerHeaderText} ${classes.nowrapCell}`}>S.No.</TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.outerHeaderText} ${classes.nowrapCell}`}>Case No.</TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.outerHeaderText} ${classes.docNameCell}`}>Document Name</TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.outerHeaderText} ${classes.uploadedByCell}`}>Uploaded By</TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.outerHeaderText} ${classes.nowrapCell}`}>Upload Date</TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.outerHeaderText} ${classes.nowrapCell}`}>Review Date</TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.outerHeaderText} ${classes.nowrapCell}`}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderTableBody()}</TableBody>
            </Table>

            {totalCount > 0 && (
              <TablePagination
                rowsPerPageOptions={[pageSize]}
                component="div"
                count={totalCount}
                rowsPerPage={pageSize}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={() => { }}
              />
            )}
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
